import React, { type ReactNode, useCallback, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';

import styles from './Tooltip.module.scss';

type Props = {
  title: ReactNode;
  children: ReactNode;
  className?: string;
  maxWidth?: number;
};

const Tooltip: React.FC<Props> = ({ title, children, className, maxWidth = 240 }: Props) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number; placement: 'top' | 'bottom' }>({ top: 0, left: 0, placement: 'bottom' });
  const anchorRef = useRef<HTMLSpanElement>(null);

  const updatePosition = useCallback(() => {
    if (!anchorRef.current) { return; }
    const rect = anchorRef.current.getBoundingClientRect();
    const estimatedHeight = 80;
    const spaceBelow = window.innerHeight - rect.bottom;
    const placement: 'top' | 'bottom' = spaceBelow < estimatedHeight + 12 ? 'top' : 'bottom';

    let left = rect.left + rect.width / 2 - maxWidth / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - maxWidth - 8));

    setCoords({
      top: placement === 'bottom' ? rect.bottom + 8 : rect.top - 8,
      left,
      placement,
    });
  }, [maxWidth]);

  useLayoutEffect(() => {
    if (open) { updatePosition(); }
  }, [open, updatePosition]);

  return (
    <span
      ref={anchorRef}
      className={classNames(styles.wrapper, className)}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      tabIndex={0}
      role="button"
      aria-label={typeof title === 'string' ? title : undefined}
    >
      {children}
      {open &&
        ReactDOM.createPortal(
          <span
            className={classNames(styles.tooltip, styles[coords.placement])}
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
              maxWidth,
              transform: coords.placement === 'top' ? 'translateY(-100%)' : undefined,
            }}
          >
            {title}
          </span>,
          document.body,
        )}
    </span>
  );
};

export default Tooltip;
